import React, { useState, useEffect } from "react";
import { Form, Input, Button, Checkbox, message, Progress, Modal } from "antd";

import { useRequest } from "ahooks";
import { JSEncrypt } from "jsencrypt";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { authService } from "../../../services/authService";
import locale from "@aligov/global-locale";
import {
  UserOutlined,
  LockOutlined,
  LockFilled,
  NumberOutlined,
} from "@ant-design/icons";

import "./index.css";

const FormItem = Form.Item;

const ResetPwdPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0); // 倒计时秒数

  const [passwordStrength, setPasswordStrength] = useState(0);
  const { login, isAuthenticated, user } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // 初始化时设置多语言
  useEffect(() => {
    // const lang = getCookie("x-hng");
    const { lang } = locale.getLocale();

    if (lang) {
      i18n.changeLanguage(lang);
    }
  }, []);

  useEffect(() => {
    // if (!isAuthenticated) {
    //   navigate("/login");
    // } else {
    // 检查是否有记住的用户名

    if (user?.loginId) {
      form.setFieldsValue({ loginId: user.loginId });
    }
    // }
  }, [isAuthenticated, navigate, form]);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const getPasswordStrengthText = (strength) => {
    if (strength < 50) return t("login.passwordStrength.weak");
    if (strength < 75) return t("login.passwordStrength.medium");
    return t("login.passwordStrength.strong");
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 50) return "#ff4d4f";
    if (strength < 75) return "#faad14";
    return "#52c41a";
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setPasswordStrength(calculatePasswordStrength(password));
  };

  // 自定义校验规则：确认密码是否与新密码一致
  const validateConfirmPassword = async (_, value) => {
    if (!value) {
      return Promise.reject(new Error(t('login.inputNewPassword')));
    }
    const newPassword = form.getFieldValue("newPassword");
    if (value !== newPassword) {
      return Promise.reject(new Error(t('login.passwordNotMatch')));
    }
    return Promise.resolve();
  };

  // 开始倒计时
  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 发送验证码
  const handleSendCode = async () => {
    try {
      // const fieldsToValidate = ["phone"]; // 或 'email'
      // await form.validateFields(fieldsToValidate);

      const loginId = form.getFieldValue("loginId");
      // 防止重复点击
      if (countdown > 0) return;

      setLoading(true);

      // 发送验证码
      axios.post(`/cnmember/api/account/user/resetPassword/emailValidCode`, {
        loginId,
      });

      // message.success("验证码已发送");
      startCountdown(); // 启动倒计时
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);

    const { loginId, oldPassword, newPassword, password2, code } = values;
    try {
      const success = await resetPassword(loginId, oldPassword, newPassword, password2, code);
      if (success) {
        message.success(t("login.loginSuccess"));

        navigate("/login");
        // // 判断是否有重定向链接
        // const params = new URLSearchParams(window.location.search);
        // const redirect = params.get("redirectUrl");

        // if (redirect) {
        //   navigate(decodeURIComponent(redirect));
        // } else {
        //   navigate("/");
        // }
      } else {
        message.error(t("login.loginFailed"));
      }
    } catch (error) {
      console.error(t("login.loginError"), error);
      message.error(error.message || error.errorMessage || t("login.loginError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-form-container">
        <h1 className="login-title">{t('login.passwordReset')}</h1>
        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="loginId"
            rules={[{ required: true, message: t("login.usernameRequired") }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t("login.username")}
            />
          </Form.Item>

          <Form.Item
            name="oldPassword"
            rules={[{ required: true, message: t("login.passwordRequired") }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t("login.inputOldPassword")}
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            rules={[{ required: true, message: t("login.passwordRequired") }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t("login.inputNewPassword")}
              onChange={handlePasswordChange}
            />
          </Form.Item>

          <Form.Item
            name="password2"
            rules={[
              { required: true, message: t("login.confirmNewPassword") },
              {
                validator: validateConfirmPassword,
                message: t('login.passwordNotMatch'),
              }, // 使用自定义校验
            ]}
          >
            <Input.Password
              prefix={<LockFilled />}
              placeholder={t("login.confirmNewPassword")}
            />
          </Form.Item>

          {/* 验证码输入 + 发送按钮 */}
          {/* <Form.Item
            name="code"
            rules={[{ required: true, message: "请输入验证码" }]}
          >
            <Input
              prefix={<NumberOutlined />}
              addonAfter={
                <Button
                  type="link"
                  loading={loading}
                  onClick={handleSendCode}
                  disabled={countdown > 0}
                  style={{ padding: 0 }}
                  size="small"
                >
                  {countdown > 0 ? `${countdown}s 后重发` : "发送验证码"}
                </Button>
              }
              placeholder="请输入验证码"
              maxLength={6}
            />
          </Form.Item> */}

          {passwordStrength > 0 && (
            <div className="password-strength">
              <div
                style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
              >
                {getPasswordStrengthText(passwordStrength)}
              </div>
              <Progress
                percent={passwordStrength}
                showInfo={false}
                strokeColor={getPasswordStrengthColor(passwordStrength)}
                size="small"
              />
            </div>
          )}

          <Form.Item>
            <a style={{ float: "right" }} href="/login">
              {t('login.goToLogin')}
            </a>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ width: "100%" }}
            >
              {t('login.passwordReset')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default ResetPwdPage;

function resetPassword(loginId, oldPwd, newPwd, password2, validCode) {
  if (String(newPwd).trim() !== String(password2).trim()) {
    return Promise.reject(new Error(t('login.passwordNotMatch')));
  }

  return authService.resetPassword(loginId, oldPwd, newPwd, password2, validCode);
}
